import { useState, useEffect } from 'react';

/**
 * Breakpoint values in pixels matching Tailwind config
 */
export const breakpoints = {
  xs: 375,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  xlg: 1440,
  '2xl': 1536,
  '2k': 1920,
} as const;

export type BreakpointKey = keyof typeof breakpoints;

/**
 * Custom hook to detect if a media query matches
 * @param query - CSS media query string (e.g., '(min-width: 768px)')
 * @returns boolean indicating if the query matches
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    // SSR-safe initial value
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);

    // Set initial value
    setMatches(mediaQuery.matches);

    // Handler for changes
    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
  }, [query]);

  return matches;
}

/**
 * Hook to check if viewport is at least a certain breakpoint
 * @param breakpoint - Breakpoint key from Tailwind config
 * @returns boolean indicating if viewport is >= breakpoint
 */
export function useBreakpoint(breakpoint: BreakpointKey): boolean {
  const query = `(min-width: ${breakpoints[breakpoint]}px)`;
  return useMediaQuery(query);
}

/**
 * Hook to check if viewport is below a certain breakpoint
 * @param breakpoint - Breakpoint key from Tailwind config
 * @returns boolean indicating if viewport is < breakpoint
 */
export function useMaxBreakpoint(breakpoint: BreakpointKey): boolean {
  const query = `(max-width: ${breakpoints[breakpoint] - 1}px)`;
  return useMediaQuery(query);
}

/**
 * Hook to check if viewport is between two breakpoints
 * @param min - Minimum breakpoint (inclusive)
 * @param max - Maximum breakpoint (exclusive)
 * @returns boolean indicating if viewport is within range
 */
export function useBetweenBreakpoints(min: BreakpointKey, max: BreakpointKey): boolean {
  const query = `(min-width: ${breakpoints[min]}px) and (max-width: ${breakpoints[max] - 1}px)`;
  return useMediaQuery(query);
}

/**
 * Hook returning the current active breakpoint
 * @returns Current breakpoint key
 */
export function useCurrentBreakpoint(): BreakpointKey {
  const is2k = useBreakpoint('2k');
  const is2xl = useBreakpoint('2xl');
  const isXlg = useBreakpoint('xlg');
  const isXl = useBreakpoint('xl');
  const isLg = useBreakpoint('lg');
  const isMd = useBreakpoint('md');
  const isSm = useBreakpoint('sm');
  const isXs = useBreakpoint('xs');

  if (is2k) return '2k';
  if (is2xl) return '2xl';
  if (isXlg) return 'xlg';
  if (isXl) return 'xl';
  if (isLg) return 'lg';
  if (isMd) return 'md';
  if (isSm) return 'sm';
  if (isXs) return 'xs';
  return 'xs'; // Default for very small screens
}

/**
 * Convenience hooks for common breakpoint checks
 */
export function useIsMobile(): boolean {
  return useMaxBreakpoint('md');
}

export function useIsTablet(): boolean {
  return useBetweenBreakpoints('md', 'lg');
}

export function useIsDesktop(): boolean {
  return useBreakpoint('lg');
}

export function useIsWideScreen(): boolean {
  return useBreakpoint('xlg');
}

export function useIsUltraWide(): boolean {
  return useBreakpoint('2k');
}

/**
 * Hook to detect device orientation
 * @returns 'portrait' | 'landscape'
 */
export function useOrientation(): 'portrait' | 'landscape' {
  const isLandscape = useMediaQuery('(orientation: landscape)');
  return isLandscape ? 'landscape' : 'portrait';
}

/**
 * Hook to detect if user prefers reduced motion
 * @returns boolean indicating preference for reduced motion
 */
export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

/**
 * Hook to detect if user prefers dark color scheme
 * @returns boolean indicating preference for dark mode
 */
export function usePrefersDarkMode(): boolean {
  return useMediaQuery('(prefers-color-scheme: dark)');
}

/**
 * Hook to detect touch-capable devices
 * @returns boolean indicating if device supports touch
 */
export function useIsTouchDevice(): boolean {
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    setIsTouch(
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      // @ts-ignore - msMaxTouchPoints is IE/Edge specific
      navigator.msMaxTouchPoints > 0
    );
  }, []);

  return isTouch;
}

export default useMediaQuery;
